/**
 * Quick ToDo Offline Support
 * - Caches todo data in IndexedDB for offline viewing
 * - Queues mutations (create, update, toggle) for sync when online
 * - Registers a service worker for asset caching
 */

const QT_DB_NAME = "QuickTodoDB";
const QT_DB_VERSION = 1;
const QT_STORE = "todos";
const QT_QUEUE_STORE = "sync_queue";

class QtOffline {
	constructor() {
		this.db = null;
		this._init_db();
		this._register_sw();
		this._setup_online_listener();
	}

	_init_db() {
		const request = indexedDB.open(QT_DB_NAME, QT_DB_VERSION);
		request.onupgradeneeded = (e) => {
			const db = e.target.result;
			if (!db.objectStoreNames.contains(QT_STORE)) {
				db.createObjectStore(QT_STORE, { keyPath: "name" });
			}
			if (!db.objectStoreNames.contains(QT_QUEUE_STORE)) {
				db.createObjectStore(QT_QUEUE_STORE, {
					keyPath: "id",
					autoIncrement: true,
				});
			}
		};
		request.onsuccess = (e) => {
			this.db = e.target.result;
		};
		request.onerror = (e) => {
			console.warn("[QtOffline] IndexedDB init failed", e);
		};
	}

	_register_sw() {
		if ("serviceWorker" in navigator) {
			navigator.serviceWorker
				.register("/assets/eternal_support/js/qt_sw.js")
				.catch((err) => console.warn("[QtOffline] SW registration failed", err));
		}

		// Inject manifest and apple PWA meta tags
		if (!document.querySelector('link[rel="manifest"]')) {
			const link = document.createElement("link");
			link.rel = "manifest";
			link.href = "/assets/eternal_support/manifest.json";
			document.head.appendChild(link);
		}
		if (!document.querySelector('meta[name="apple-mobile-web-app-capable"]')) {
			const meta1 = document.createElement("meta");
			meta1.name = "apple-mobile-web-app-capable";
			meta1.content = "yes";
			document.head.appendChild(meta1);

			const meta2 = document.createElement("meta");
			meta2.name = "apple-mobile-web-app-status-bar-style";
			meta2.content = "default";
			document.head.appendChild(meta2);

			const meta3 = document.createElement("meta");
			meta3.name = "apple-mobile-web-app-title";
			meta3.content = "Quick ToDo";
			document.head.appendChild(meta3);

			const link2 = document.createElement("link");
			link2.rel = "apple-touch-icon";
			link2.href = "/assets/eternal_support/images/Eternal-tech-favicon.png";
			document.head.appendChild(link2);
		}
	}

	_setup_online_listener() {
		window.addEventListener("online", () => {
			this.flush_queue();
		});
	}

	is_offline() {
		return !navigator.onLine;
	}

	// Save todos to IndexedDB (called after every successful fetch)
	async save_todos(todos) {
		if (!this.db) return;
		const tx = this.db.transaction(QT_STORE, "readwrite");
		const store = tx.objectStore(QT_STORE);
		store.clear();
		todos.forEach((todo) => store.put(todo));
	}

	// Load todos from IndexedDB (used when offline)
	async load_todos() {
		if (!this.db) return [];
		return new Promise((resolve) => {
			const tx = this.db.transaction(QT_STORE, "readonly");
			const store = tx.objectStore(QT_STORE);
			const request = store.getAll();
			request.onsuccess = () => resolve(request.result || []);
			request.onerror = () => resolve([]);
		});
	}

	// Add an action to the sync queue (used when offline)
	async enqueue(action) {
		if (!this.db) return;
		const tx = this.db.transaction(QT_QUEUE_STORE, "readwrite");
		const store = tx.objectStore(QT_QUEUE_STORE);
		store.add({
			...action,
			timestamp: Date.now(),
		});
	}

	// Flush the sync queue when back online
	async flush_queue() {
		if (!this.db || !navigator.onLine) return;
		const tx = this.db.transaction(QT_QUEUE_STORE, "readonly");
		const store = tx.objectStore(QT_QUEUE_STORE);
		const request = store.getAll();

		request.onsuccess = async () => {
			const items = request.result || [];
			if (!items.length) return;

			for (const item of items) {
				try {
					await frappe.call({
						method: item.method,
						args: item.args,
					});
				} catch (e) {
					console.warn("[QtOffline] Sync failed for", item, e);
					return;
				}
			}

			const clearTx = this.db.transaction(QT_QUEUE_STORE, "readwrite");
			clearTx.objectStore(QT_QUEUE_STORE).clear();

			if (window._qt_todo) {
				frappe.show_alert({ message: __("Offline changes synced"), indicator: "green" });
				window._qt_todo.refresh();
			}
		};
	}

	// Get pending queue count
	async get_queue_count() {
		if (!this.db) return 0;
		return new Promise((resolve) => {
			const tx = this.db.transaction(QT_QUEUE_STORE, "readonly");
			const store = tx.objectStore(QT_QUEUE_STORE);
			const request = store.count();
			request.onsuccess = () => resolve(request.result);
			request.onerror = () => resolve(0);
		});
	}
}

// Global singleton
window._qt_offline = new QtOffline();
